import { useState } from 'react';
import { X, Search, ExternalLink } from 'lucide-react';

export const NistGlossaryModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // NIST Glossary Terms Database (key cybersecurity terms)
  const nistGlossary = [
    {
      term: "Risk Management",
      definition: "The process of identifying, assessing, and controlling threats to an organization's capital and earnings.",
      source: "NIST SP 800-30",
      link: "https://csrc.nist.gov/glossary/term/risk_management"
    },
    {
      term: "Cybersecurity",
      definition: "Prevention of damage to, protection of, and restoration of computers, electronic communications systems, electronic communications services, wire communication, and electronic communication.",
      source: "NIST SP 800-53",
      link: "https://csrc.nist.gov/glossary/term/cybersecurity"
    },
    {
      term: "Security Control",
      definition: "A safeguard or countermeasure prescribed for an information system or an organization designed to protect the confidentiality, integrity, and availability of its information.",
      source: "NIST SP 800-53",
      link: "https://csrc.nist.gov/glossary/term/security_control"
    },
    {
      term: "Risk Assessment",
      definition: "The process of identifying, estimating, and prioritizing risks to organizational operations, organizational assets, individuals, other organizations, and the Nation.",
      source: "NIST SP 800-30",
      link: "https://csrc.nist.gov/glossary/term/risk_assessment"
    },
    {
      term: "Continuous Monitoring",
      definition: "Maintaining ongoing awareness of information security, vulnerabilities, and threats to support organizational risk management decisions.",
      source: "NIST SP 800-137",
      link: "https://csrc.nist.gov/glossary/term/continuous_monitoring"
    },
    {
      term: "Authorization",
      definition: "The official management decision given by a senior organizational official to authorize operation of an information system.",
      source: "NIST SP 800-37",
      link: "https://csrc.nist.gov/glossary/term/authorization"
    },
    {
      term: "FedRAMP",
      definition: "Federal Risk and Authorization Management Program - A government-wide program that provides a standardized approach to security assessment, authorization, and continuous monitoring for cloud products and services.",
      source: "FedRAMP",
      link: "https://www.fedramp.gov"
    },
    {
      term: "OSCAL",
      definition: "Open Security Controls Assessment Language - A set of formats expressed in XML, JSON, and YAML for representing security controls, control catalogs, control baselines, and assessment results.",
      source: "NIST",
      link: "https://pages.nist.gov/OSCAL/"
    },
    {
      term: "Security Framework",
      definition: "A structured approach to cybersecurity that includes standards, guidelines, and best practices to manage cybersecurity risk.",
      source: "NIST Cybersecurity Framework",
      link: "https://www.nist.gov/cyberframework"
    },
    {
      term: "Threat Intelligence",
      definition: "Threat information that has been aggregated, transformed, analyzed, interpreted, or enriched to provide the necessary context for decision-making processes.",
      source: "NIST SP 800-150",
      link: "https://csrc.nist.gov/glossary/term/threat_intelligence"
    }
  ];

  const handleSearch = (query) => {
    setSearchTerm(query);
    setIsSearching(true);
    
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Filter glossary terms
    const results = nistGlossary.filter(item =>
      item.term.toLowerCase().includes(query.toLowerCase()) ||
      item.definition.toLowerCase().includes(query.toLowerCase())
    );

    setTimeout(() => {
      setSearchResults(results);
      setIsSearching(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <div className="netflix-card max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-netflix-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">NIST Cybersecurity Glossary</h2>
            <button 
              onClick={onClose}
              className="text-netflix-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-netflix-gray-400" />
            <input
              type="text"
              placeholder="Search NIST cybersecurity terms..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-netflix-gray-200 border border-netflix-gray-300 rounded text-white placeholder-netflix-gray-400 focus:outline-none focus:border-netflix-red transition-colors"
              autoFocus
            />
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-96">
          {isSearching ? (
            <div className="text-center py-8">
              <div className="inline-block w-6 h-6 border-2 border-netflix-red border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-netflix-gray-400">Searching NIST glossary...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-4">
              {searchResults.map((result, index) => (
                <div key={index} className="border border-netflix-gray-200 rounded-lg p-4 hover:border-netflix-red/30 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{result.term}</h3>
                      <p className="text-netflix-gray-400 mb-3 leading-relaxed">{result.definition}</p>
                      <div className="flex items-center space-x-4">
                        <span className="netflix-badge netflix-badge-secondary text-xs">{result.source}</span>
                        <a 
                          href={result.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-netflix-red hover:text-white transition-colors text-sm"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View Official Definition
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm.length >= 2 ? (
            <div className="text-center py-8">
              <p className="text-netflix-gray-400">No terms found for "{searchTerm}"</p>
              <p className="text-netflix-gray-400 text-sm mt-2">Try searching for: Risk, Security, Control, Framework, Assessment</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-netflix-gray-400">Search for NIST cybersecurity terms and definitions</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                {['Risk Management', 'Cybersecurity', 'Security Control', 'FedRAMP', 'OSCAL', 'Authorization'].map((term) => (
                  <button
                    key={term}
                    onClick={() => handleSearch(term)}
                    className="netflix-badge netflix-badge-secondary text-xs hover:bg-netflix-red transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-netflix-gray-200 bg-netflix-gray-100">
          <p className="text-netflix-gray-400 text-sm text-center">
            Definitions sourced from official NIST publications and cybersecurity standards
          </p>
        </div>
      </div>
    </div>
  );
};